import {Component, EventEmitter, Inject, OnInit, Output, PLATFORM_ID, ViewEncapsulation} from '@angular/core';
import {AbstractControl, FormBuilder, ReactiveFormsModule, Validators} from '@angular/forms';
import {NgbActiveModal, NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {RegisterComponent} from '../register/register.component';
import {environment} from '../../../../environments/environment';
import {Router} from '@angular/router';
import {SharedModule} from '../../../shared/module/shared.module';
import {SocialAuthService, FacebookLoginProvider} from "@abacritt/angularx-social-login";
import {Subject, takeUntil} from 'rxjs';
import {ToastService, SubscribersService, TokenStorageService, LoaderService, GoogleCliService} from '@coreServices';
import {AuthService} from '@coreApiServices';
import {RegexPatternConstants, STORAGE} from '@coreConstant';
import {SessionModel, UserModel} from '@coreModels';
import {isPlatformBrowser} from "@angular/common";

declare var google: any;

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [ReactiveFormsModule, SharedModule],
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss', '../auth.component.scss'],
  providers: [NgbActiveModal, SocialAuthService],
  encapsulation: ViewEncapsulation.None
})
export class LoginComponent implements OnInit {

  loginForm: any;
  scriptLoaded = false;
  RegexPatternConstants = RegexPatternConstants;
  @Output() type: EventEmitter<string> = new EventEmitter();
  destroy$ = new Subject<void>();
  googleInitialized!: boolean;
  googlePrompted!: boolean;

  constructor(
    public activeModal: NgbActiveModal,
    private fb: FormBuilder,
    private modalService: NgbModal,
    private authService: AuthService,
    private tokenStorageService: TokenStorageService,
    private subscriberService: SubscribersService,
    private router: Router,
    private toastService: ToastService,
    private socialAuthService: SocialAuthService,
    public loaderService: LoaderService,
    @Inject(PLATFORM_ID) private platformId: any,
    private googleCliService: GoogleCliService
  ) {
    this.createForm();
  }

  ngOnInit(): void {
  }

  ngAfterViewInit() {
    if (isPlatformBrowser(this.platformId)) {
      this.googleCliService.loadGoogleScript().then(() => {
        this.scriptLoaded = true;
        this.initializeGoogleSignIn();
      }).catch((error) => {
       this.toastService.showError(error)
      });
    }
  }

  private initializeGoogleSignIn() {
    if (isPlatformBrowser(this.platformId)) {
      const buttonContainer = document.getElementById('google-signin-button');
      if (buttonContainer) {
        buttonContainer.innerHTML = ''; // Clear previous content
      }

      // ✅ Cancel any existing session before proceeding
      google.accounts.id.cancel();

      // ✅ Ensure Google Sign-In is initialized only once
      if (!this.googleInitialized) {
        google.accounts.id.initialize({
          client_id: environment.GOOGLE_CLIENT_ID, // Replace with your actual client ID
          callback: this.googleLogin.bind(this),  // Your login callback
          use_fedcm_for_prompt: true,             // Enable FedCM explicitly
        });
        this.googleInitialized = true;
      }

      // ✅ Ensure `prompt()` runs only once per session
      if (!this.googlePrompted) {
        this.googlePrompted = true;
        google.accounts.id.prompt(() => {});
      }

      // ✅ Render the Google Sign-In button
      if (buttonContainer) {
        google.accounts.id.renderButton(buttonContainer, {
          theme: 'outline',
          size: 'large',
          type: 'standard',
          shape: 'rectangular',
          text: 'signin_with',
        });

        buttonContainer.style.visibility = 'visible'; // Ensure the button is visible
      }
    }
  }

  /**
   * This method is used to create login formgroup
   */
  createForm() {
    this.loginForm = this.fb.group({
      email: ['', [Validators.required, Validators.pattern(RegexPatternConstants.EMAIL_REGEX)]],
      password: ['', [Validators.required, Validators.minLength(6), this.TrimmedRequiredValidator]]
    });
  }

  /**
   * This method is used to authenticate user credentials
   */
  login() {
    let loginModel = {
      username: encodeURIComponent(this.loginForm.value.email),
      password: this.loginForm.value.password,
      client_id: environment.FRONTEND_CLIENT_ID,
      client_secret: environment.FRONTEND_CLIENT_SECRET,
      grant_type: environment.GRANT_TYPE
    }

    this.authService.login(loginModel).pipe(takeUntil(this.destroy$)).subscribe({
      next: (sessionModel: SessionModel) => {
        this.tokenStorageService.removeItemStorage(STORAGE.SAMPLE_ORDER_KEY);
        if (sessionModel.access_token !== undefined) {
          this.tokenStorageService.setToken(sessionModel.access_token);

          this.loaderService.show()
          this.authService.getLoginUserDetail().pipe(takeUntil(this.destroy$)).subscribe({
            next: (userModel: UserModel) => {
              this.tokenStorageService.setUser(userModel);
              this.loginForm.reset();
              this.modalService.dismissAll();
              this.loaderService.hide()
              this.subscriberService.setUserLoggedInStatus(true);

              if (this.tokenStorageService.getItemStorage(STORAGE.ACCEPT_REQUIRE_KEY)) {
                this.router.navigate(['/account/quotes/' + this.tokenStorageService.getItemStorage(STORAGE.ACCEPT_REQUIRE_KEY)]);
              } else if (this.tokenStorageService.getItemStorage(STORAGE.DELIVERY_DATE_REQUIRED_KEY)) {
                this.router.navigate(['/account/orders/' + this.tokenStorageService.getItemStorage(STORAGE.DELIVERY_DATE_REQUIRED_KEY) + '?order_status=AAW'])
              } else if (this.tokenStorageService.getItemStorage(STORAGE.REDIRECT_URL_KEY)) {
                this.router.navigate([this.tokenStorageService.getItemStorage(STORAGE.REDIRECT_URL_KEY)]);
                this.tokenStorageService.removeItemStorage(STORAGE.REDIRECT_URL_KEY);
              }
            },
            error: (error) => {
              if (error.status == 422) {
                this.toastService.showError(error);
                this.loaderService.hide()
              } else if (error.status == 401) {
                // this.toastService.showError(error.error.data.message);
              }
              // this.spinnerService.hide()
            }
          })
        }
      },
      error: (error) => {
        if (error.status == 422) {
          this.toastService.showError(error);
        } else if (error.status == 401) {
          // this.toastService.showError(error.error?.data?.message || error.error?.message);
        }
        // this.spinnerService.hide();
      }
    })
  }

  googleLogin(response: any): void {
    this.loaderService.show()
    // You need to get the ID token from the response
    const idToken = response.credential;
    if (!idToken) {
      this.toastService.showError('Google ID Token is missing');
      this.loaderService.hide()
      return;
    }
    let loginModelGL = {
      id_token: idToken
    };
    // Call your backend API with the Google ID token
    this.authService.loginWithGoogle(loginModelGL).pipe(takeUntil(this.destroy$)).subscribe({
      next: (sessionModel: any) => {
        if (sessionModel.access_token) {
          this.tokenStorageService.setToken(sessionModel.access_token);

          // Now retrieve user details using the session token
          this.authService.getLoginUserDetail().pipe(takeUntil(this.destroy$)).subscribe({
            next: (userModel: any) => {
              this.tokenStorageService.setUser(userModel);
              this.loginForm.reset();
              this.modalService.dismissAll();
              this.subscriberService.setUserLoggedInStatus(true);
              this.loaderService.hide()
              if (this.tokenStorageService.getItemStorage(STORAGE.ACCEPT_REQUIRE_KEY)) {
                this.router.navigate(['/account/quotes/' + this.tokenStorageService.getItemStorage(STORAGE.ACCEPT_REQUIRE_KEY)]);
              } else if (this.tokenStorageService.getItemStorage(STORAGE.DELIVERY_DATE_REQUIRED_KEY)) {
                this.router.navigate(['/account/orders/' + this.tokenStorageService.getItemStorage(STORAGE.DELIVERY_DATE_REQUIRED_KEY) + '?order_status=AAW'])
              } else if (this.tokenStorageService.getItemStorage(STORAGE.REDIRECT_URL_KEY)) {
                this.router.navigate([this.tokenStorageService.getItemStorage(STORAGE.REDIRECT_URL_KEY)]);
                this.tokenStorageService.removeItemStorage(STORAGE.REDIRECT_URL_KEY);
              }
            },
            error: (error) => {
              if (error.status == 422) {
                this.toastService.showError(error);
                this.loaderService.hide()
              } else if (error.status == 401) {
                // this.toastService.showError(error.error.data.message);
              }
              this.loaderService.hide()
            }
          });
        }
      },
      error: (error) => {
     this.toastService.showError(error)
        this.loaderService.hide()
      }
    });
  }

  handleError(error: any) {
    if (error.status === 422) {
      this.toastService.showError(error);
      this.loaderService.hide()
    } else if (error.status === 401) {
      this.toastService.showError(error.error.data.message);
      this.loaderService.hide()
    }
  }

  fbLogin() {
    const fbLoginOptions = {
      scope: 'public_profile,email'
    };
    this.socialAuthService.signIn(FacebookLoginProvider.PROVIDER_ID, fbLoginOptions).then((userData) => {
      let loginModelFB = {
        access_token: userData.authToken
      };
      this.authService.loginWithFacebook(loginModelFB).pipe(takeUntil(this.destroy$)).subscribe({
        next: (sessionModel: SessionModel) => {
          if (sessionModel.access_token !== undefined) {
            this.tokenStorageService.setToken(sessionModel.access_token);
            this.authService.getLoginUserDetail().pipe(takeUntil(this.destroy$)).subscribe({
              next: (userModel: UserModel) => {
                this.tokenStorageService.setUser(userModel);
                this.loginForm.reset();
                this.modalService.dismissAll();
                this.subscriberService.setUserLoggedInStatus(true);
              },
              error: (error) => {
                if (error.status == 422) {
                  this.toastService.showError(error);
                } else if (error.status == 401) {
                  this.toastService.showError(error.error.data.message);
                }
              }
            })
          }
        }, error: (error) => {
          if (error.status == 422) {
            this.toastService.showError(error);
          } else if (error.status == 401) {
            this.toastService.showError(error.error.data.message);
          }
        }
      })
    }).catch((error: any) => {
      this.toastService.showError(error);
    });
  }

  switchForgotPassword() {
    this.type.emit('forgot-password');
  }

  showRegisterModal() {
    this.modalService.dismissAll();
    this.modalService.open(RegisterComponent);
  }

  TrimmedRequiredValidator(control: AbstractControl) {
    if (control.value && control.value.trim() === '') {
      return {required: true};
    }
    return null;
  }


  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
